import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Shield, 
  TrendingUp, 
  CheckCircle, 
  Building, 
  CreditCard,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Lock,
  Star,
  ArrowRight,
  Menu,
  X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-credit-eval/artifacts/8w7azhpp_a8299614-dead-4387-811d-09deff6f81fe.png";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_54ab0d0a-a426-41ea-87ec-93f8278f020a/artifacts/ppfdudmf_6266252-uhd_3840_2160_25fps.mp4";
const ILLUSTRATION_URL = "https://customer-assets.emergentagent.com/job_54ab0d0a-a426-41ea-87ec-93f8278f020a/artifacts/agx319q4_unnamed-1.png";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } }
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="relative -my-4">
          <img src={LOGO_URL} alt="Line of Luxury" className="h-20 md:h-28" />
        </Link>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <a href="#services" className="nav-link font-sans text-sm uppercase tracking-wider" data-testid="nav-services">Services</a>
          <a href="#why-us" className="nav-link font-sans text-sm uppercase tracking-wider" data-testid="nav-why-us">Why Us</a>
          <a href="#faq" className="nav-link font-sans text-sm uppercase tracking-wider" data-testid="nav-faq">FAQ</a>
          <a href="#contact" className="nav-link font-sans text-sm uppercase tracking-wider" data-testid="nav-contact">Contact</a>
          <Link 
            to="/evaluation" 
            className="btn-primary px-6 py-3 text-sm"
            data-testid="nav-evaluation-btn"
          >
            Start Evaluation
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-white/5 py-6 px-6">
          <nav className="flex flex-col gap-4">
            <a href="#services" className="nav-link font-sans text-sm uppercase tracking-wider py-2" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#why-us" className="nav-link font-sans text-sm uppercase tracking-wider py-2" onClick={() => setMobileMenuOpen(false)}>Why Us</a>
            <a href="#faq" className="nav-link font-sans text-sm uppercase tracking-wider py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <a href="#contact" className="nav-link font-sans text-sm uppercase tracking-wider py-2" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <Link 
              to="/evaluation" 
              className="btn-primary px-6 py-3 text-sm text-center mt-4"
              data-testid="mobile-evaluation-btn"
            >
              Start Evaluation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="hero-section relative min-h-screen flex items-center justify-center" data-testid="hero-section">
    {/* Video Background */}
    <video 
      autoPlay 
      muted 
      loop 
      playsInline
      className="hero-video"
      data-testid="hero-video"
    >
      <source src={VIDEO_URL} type="video/mp4" />
    </video>
    
    {/* Overlay */}
    <div className="video-overlay"></div>
    
    {/* Content */}
    <div className="relative z-10 max-w-7xl mx-auto px-6 text-center pt-20">
      <motion.div {...fadeInUp}>
        <img 
          src={LOGO_URL} 
          alt="Line of Luxury" 
          className="h-24 md:h-32 mx-auto mb-8"
          data-testid="hero-logo"
        />
      </motion.div>
      
      <motion.h1 
        className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        data-testid="hero-headline"
      >
        Elevate Your Credit.<br />
        <span className="gold-gradient-text">Unlock Financial Freedom.</span>
      </motion.h1>
      
      <motion.p 
        className="text-lg md:text-xl text-[#A1A1AA] max-w-2xl mx-auto mb-10 font-sans"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        data-testid="hero-subheadline"
      >
        Expert credit repair, business funding, and financial solutions designed to help you win.
      </motion.p>
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <Link 
          to="/evaluation"
          className="btn-primary inline-flex items-center gap-2 text-lg"
          data-testid="hero-cta-btn"
        >
          Start Your Evaluation
          <ArrowRight size={20} />
        </Link>
      </motion.div>
      
      {/* Trust Indicators */}
      <motion.div 
        className="flex flex-wrap justify-center gap-8 mt-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <div className="trust-badge">
          <Lock size={18} />
          <span>Secure & Encrypted</span>
        </div>
        <div className="trust-badge">
          <Shield size={18} />
          <span>Trusted Nationwide</span>
        </div>
        <div className="trust-badge">
          <Star size={18} />
          <span>Expert Support</span>
        </div>
      </motion.div>
    </div>
    
    {/* Scroll Indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <ChevronDown size={32} className="text-[#D4AF37]" />
    </div>
  </section>
);

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <CreditCard size={40} className="text-[#D4AF37]" />,
      title: "Credit Repair",
      description: "Professional dispute strategies to remove negative items and boost your credit score fast.",
      testId: "service-credit-repair"
    },
    {
      icon: <Building size={40} className="text-[#D4AF37]" />,
      title: "Business Funding",
      description: "Access capital for your business with our funding solutions and expert guidance.",
      testId: "service-business-funding"
    }
  ];

  return (
    <section id="services" className="py-24 bg-[#0A0A0A] noise-bg" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
        >
          <h2 className="font-serif text-3xl md:text-4xl mb-4" data-testid="services-title">
            Our <span className="gold-gradient-text">Premium Services</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Comprehensive financial solutions tailored to elevate your financial standing
          </p>
        </motion.div>
        
        <motion.div 
          className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card p-8 text-center"
              variants={fadeInUp}
              data-testid={service.testId}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#111111] mb-6">
                {service.icon}
              </div>
              <h3 className="font-serif text-xl mb-4 text-white">{service.title}</h3>
              <p className="text-[#A1A1AA] font-sans text-sm leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUsSection = () => {
  const reasons = [
    { icon: <CheckCircle size={24} className="text-[#D4AF37]" />, text: "Proven dispute strategies that deliver results" },
    { icon: <Shield size={24} className="text-[#D4AF37]" />, text: "Fast, secure, and confidential process" },
    { icon: <TrendingUp size={24} className="text-[#D4AF37]" />, text: "Personalized credit improvement plans" },
    { icon: <Building size={24} className="text-[#D4AF37]" />, text: "Business funding opportunities available" }
  ];

  return (
    <section id="why-us" className="py-24 bg-[#111111]" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-lg overflow-hidden gold-border">
              <img 
                src={ILLUSTRATION_URL} 
                alt="Financial Freedom" 
                className="w-full"
                data-testid="why-us-illustration"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-[#D4AF37]/30 -z-10"></div>
          </motion.div>
          
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-6" data-testid="why-us-title">
              Why Choose <span className="gold-gradient-text">Line of Luxury?</span>
            </h2>
            <p className="text-[#A1A1AA] mb-8 font-sans leading-relaxed">
              Line of Luxury Credit Repair offers credit and financial consulting services 
              designed to help you take control of your financial future. Our expert team 
              is dedicated to your success.
            </p>
            
            <ul className="space-y-4">
              {reasons.map((reason, index) => (
                <li key={index} className="flex items-center gap-4" data-testid={`why-us-reason-${index}`}>
                  {reason.icon}
                  <span className="text-white font-sans">{reason.text}</span>
                </li>
              ))}
            </ul>
            
            <Link 
              to="/evaluation"
              className="btn-primary inline-flex items-center gap-2 mt-10"
              data-testid="why-us-cta-btn"
            >
              Get Started Today
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Before/After Section
const BeforeAfterSection = () => (
  <section className="py-24 bg-[#0A0A0A] noise-bg" data-testid="before-after-section">
    <div className="max-w-7xl mx-auto px-6">
      <motion.div 
        className="text-center mb-16"
        {...fadeInUp}
        viewport={{ once: true }}
      >
        <h2 className="font-serif text-3xl md:text-4xl mb-4" data-testid="before-after-title">
          Real <span className="gold-gradient-text">Results</span>
        </h2>
        <p className="text-[#A1A1AA] max-w-2xl mx-auto">
          See the transformation our clients experience with our proven credit repair strategies
        </p>
      </motion.div>
      
      <motion.div 
        className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {/* Before */}
        <div className="text-center">
          <div className="score-meter mb-4">
            <div className="score-circle score-before" data-testid="score-before">
              520
            </div>
          </div>
          <p className="font-serif text-xl text-[#EF4444]">Before</p>
          <p className="text-[#A1A1AA] text-sm">Poor Credit</p>
        </div>
        
        {/* Arrow */}
        <div className="hidden md:block">
          <ArrowRight size={48} className="text-[#D4AF37]" />
        </div>
        <div className="md:hidden">
          <ChevronDown size={48} className="text-[#D4AF37]" />
        </div>
        
        {/* After */}
        <div className="text-center">
          <div className="score-meter mb-4">
            <div className="score-circle score-after" data-testid="score-after">
              750
            </div>
          </div>
          <p className="font-serif text-xl text-[#10B981]">After</p>
          <p className="text-[#A1A1AA] text-sm">Excellent Credit</p>
        </div>
      </motion.div>
      
      <motion.p 
        className="text-center text-[#52525B] text-sm mt-12 max-w-xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        viewport={{ once: true }}
      >
        *Results may vary. Individual results depend on unique credit situations and are not guaranteed.
      </motion.p>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "How does credit repair work?",
      answer: "Our credit repair process involves analyzing your credit reports, identifying inaccurate or unfair negative items, and disputing them with the credit bureaus on your behalf. We use proven strategies to challenge items that may be hurting your score."
    },
    {
      question: "How long does credit repair take?",
      answer: "Results vary depending on your unique situation. Most clients begin seeing improvements within 30-90 days. Our team works diligently to achieve the best results as quickly as possible."
    },
    {
      question: "What is included in the $15 credit evaluation?",
      answer: "Our credit evaluation includes a comprehensive review of your credit report, identification of negative items that may be disputed, a personalized action plan, and a consultation with one of our credit specialists."
    },
    {
      question: "Is my information secure?",
      answer: "Absolutely. We use bank-level encryption to protect all your personal information. Your data is never shared with third parties and is used solely for the purpose of improving your credit."
    },
    {
      question: "Can you help with business funding?",
      answer: "Yes! In addition to credit repair, we offer business funding solutions to help entrepreneurs access the capital they need. Good personal credit often plays a role in securing business funding."
    }
  ];

  return (
    <section id="faq" className="py-24 bg-[#111111]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
        >
          <h2 className="font-serif text-3xl md:text-4xl mb-4" data-testid="faq-title">
            Frequently Asked <span className="gold-gradient-text">Questions</span>
          </h2>
          <p className="text-[#A1A1AA]">
            Get answers to common questions about our services
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-[#161616] border border-white/5 px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="faq-trigger font-sans text-left py-5 hover:no-underline text-white">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#A1A1AA] font-sans pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to Netlify Forms
      const formBody = new URLSearchParams();
      formBody.append("form-name", "contact");
      formBody.append("name", formData.name);
      formBody.append("email", formData.email);
      formBody.append("phone", formData.phone);
      formBody.append("message", formData.message);

      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });

      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#0A0A0A] noise-bg" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-6" data-testid="contact-title">
              Get In <span className="gold-gradient-text">Touch</span>
            </h2>
            <p className="text-[#A1A1AA] mb-8 font-sans leading-relaxed">
              Ready to take control of your financial future? Contact us today 
              and let our experts help you on your journey to better credit.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#161616] flex items-center justify-center">
                  <Mail size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[#52525B] text-sm uppercase tracking-wider">Email</p>
                  <a href="mailto:Lineofluxury@outlook.com" className="text-white hover:text-[#D4AF37] transition-colors">Lineofluxury@outlook.com</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#161616] flex items-center justify-center">
                  <Phone size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[#52525B] text-sm uppercase tracking-wider">Phone</p>
                  <a href="tel:941-210-1189" className="text-white hover:text-[#D4AF37] transition-colors">941-210-1189</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#161616] flex items-center justify-center">
                  <MapPin size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[#52525B] text-sm uppercase tracking-wider">Location</p>
                  <p className="text-white">Serving Clients Nationwide</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <form 
              name="contact" 
              method="POST" 
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit} 
              className="bg-[#161616] p-8 border border-white/5" 
              data-testid="contact-form"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="luxury-input"
                    placeholder="Your name"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="luxury-input"
                    placeholder="your@email.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">Phone (Optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="luxury-input"
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="luxury-input min-h-[120px] resize-none"
                    placeholder="How can we help you?"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <div className="spinner"></div>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-[#111111] border-y border-white/5" data-testid="cta-section">
    <div className="max-w-4xl mx-auto px-6 text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6" data-testid="cta-title">
          Your Financial Future <span className="gold-gradient-text">Starts Now</span>
        </h2>
        <p className="text-[#A1A1AA] text-lg mb-10 max-w-2xl mx-auto">
          Don't let bad credit hold you back. Take the first step toward financial freedom with our expert credit evaluation.
        </p>
        <Link 
          to="/evaluation"
          className="btn-primary inline-flex items-center gap-2 text-lg animate-pulse-gold"
          data-testid="cta-btn"
        >
          Start Your Evaluation – Only $15
          <ArrowRight size={20} />
        </Link>
      </motion.div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-16 bg-[#0A0A0A] border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        {/* Logo & Description */}
        <div className="md:col-span-2">
          <img src={LOGO_URL} alt="Line of Luxury" className="h-16 mb-6" />
          <p className="text-[#A1A1AA] font-sans leading-relaxed max-w-md">
            Line of Luxury LLC provides premium credit repair and business funding 
            solutions to help you achieve the credit score you deserve.
          </p>
        </div>
        
        {/* Quick Links */}
        <div>
          <h4 className="font-serif text-lg mb-4 text-white">Quick Links</h4>
          <ul className="space-y-3">
            <li><a href="#services" className="footer-link font-sans text-sm">Services</a></li>
            <li><a href="#why-us" className="footer-link font-sans text-sm">Why Choose Us</a></li>
            <li><a href="#faq" className="footer-link font-sans text-sm">FAQ</a></li>
            <li><a href="#contact" className="footer-link font-sans text-sm">Contact</a></li>
          </ul>
        </div>
        
        {/* Contact Info */}
        <div>
          <h4 className="font-serif text-lg mb-4 text-white">Contact</h4>
          <ul className="space-y-3">
            <li><a href="tel:941-210-1189" className="footer-link font-sans text-sm">941-210-1189</a></li>
            <li><a href="mailto:Lineofluxury@outlook.com" className="footer-link font-sans text-sm">Lineofluxury@outlook.com</a></li>
            <li><Link to="/evaluation" className="footer-link font-sans text-sm">Credit Evaluation</Link></li>
          </ul>
        </div>
      </div>
      
      <div className="section-divider mb-8"></div>
      
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-[#52525B] text-sm font-sans">
          © {new Date().getFullYear()} Line of Luxury LLC. All rights reserved.
        </p>
        <div className="flex gap-6">
          <a href="#" className="footer-link text-sm">Privacy Policy</a>
          <a href="#" className="footer-link text-sm">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// Main HomePage Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <HeroSection />
      <ServicesSection />
      <WhyUsSection />
      <BeforeAfterSection />
      <FAQSection />
      <ContactSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default HomePage;
